/**
 * SanPlumb — Dashboard (Fase 3c)
 *
 * Pantalla de entrada: logo isométrico animado + 3 tarjetas de los pasos
 * principales (Hunter, Ingesta, Visor) + acceso rápido a Dotación.
 *
 * Portado de `screens.jsx` (Dashboard + DashLogo iso) del diseño aprobado.
 * Logo extraído a `components/Brand.tsx` para reuso en el splash.
 */

import type { Screen } from '../../types/ui';
import { Icon } from '../Icon';
import { DashLogo } from '../Brand';

export interface DashboardProps {
  onNavigate: (s: Screen) => void;
  onOpenIngesta: () => void;
}

export function Dashboard({ onNavigate, onOpenIngesta }: DashboardProps): JSX.Element {
  return (
    <div className="dash screen-anim" data-screen-label="00 Dashboard">
      <div className="grid-bg" />
      <div className="dash-inner">
        <div className="eyebrow">
          SISTEMA DE DISEÑO SANITARIO · UNIFAMILIARES Y MULTIFAMILIARES · v1.11
        </div>

        <div className="dash-logo plumb-fx-heat">
          <DashLogo />
        </div>

        <div className="dash-sub">
          <span className="dot">●</span>AGUA FRÍA
          <span className="dot dot-ac">●</span>AGUA CALIENTE
          <span className="dot" style={{ color: 'var(--t-faint)' }}>
            ·
          </span>
          IS.010 PERÚ
        </div>

        <div className="cards">
          <button className="card tinted t-hunter active" onClick={() => onNavigate('hunter')}>
            <div className="num">PASO 01</div>
            <div className="ico">{Icon.chart}</div>
            <h3>
              Cálculo Hunter
              <br />
              IS.010
            </h3>
            <div className="desc">
              Ingresa aparatos sanitarios, obtén unidades de gasto (UG), caudal probable y
              diámetros recomendados.
            </div>
            <div className="cta">
              <span>Empieza aquí</span>
              {Icon.arrow}
            </div>
          </button>

          <button className="card tinted t-ingesta" onClick={onOpenIngesta}>
            <div className="num">PASO 02</div>
            <div className="ico">{Icon.scan}</div>
            <h3>
              Ingesta
              <br />+ Precisión NPT
            </h3>
            <div className="desc">
              Sube el plano como referencia y declara los aparatos y cotas según los manuales.
              Confirma medidas reales del baño y espesor de contrapiso.
            </div>
            <div className="cta">
              <span>Modal de confirmación</span>
              {Icon.arrow}
            </div>
          </button>

          <button className="card tinted t-viewer" onClick={() => onNavigate('viewer')}>
            <div className="num" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>PASO 03</span>
              <span style={{ display: 'flex', gap: 6 }}>
                <span className="badge-mini af">
                  <span className="sw" /> AF
                </span>
                <span className="badge-mini ac">
                  <span className="sw" /> AC
                </span>
              </span>
            </div>
            <div className="ico">{Icon.cube}</div>
            <h3>
              Visor
              <br />
              Isométrico 3D
            </h3>
            <div className="desc">
              Red 3D completa con AF azul + AC rojo, accesorios PVC/CPVC y metrado BOM separado por
              sistema.
            </div>
            <div className="cta">
              <span>Demo disponible</span>
              {Icon.arrow}
            </div>
          </button>
        </div>

        <div className="quick-actions">
          <button className="quick tinted q-dotacion" onClick={() => onNavigate('dotacion')}>
            <span>Dotación y volumetría · cisterna · tanque elevado · terma</span>
            {Icon.arrow}
          </button>
        </div>
      </div>
    </div>
  );
}
